import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Text } from "@/components/ui/baseUi/text"
import { useAttachProgramsToCoach } from "@/modules/coach/hooks/useProfessionalCoach"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { Path } from "@/modules/navigation/routes"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import {
  OnBoardingStripeModal,
  type OnBoardingStripeModalRef,
} from "@/modules/programs/components/onBoardingStripeModal"
import { UserProgramItemEntity } from "@/modules/programs/entities/userProgramItemEntity"
import { usePrograms } from "@/modules/programs/hooks/usePrograms"
import { ProgramFilter } from "@/modules/programs/types/programTypes"
import { LocalStorageKeys } from "@/modules/storage/localStorageKeys"
import { useUser } from "@/modules/users/hooks/useUser"
import CongratulationsIcon from "@images/coach/congratulations.svg"
import RoundsCustomIcon from "@images/coach/rounds-custom.svg"
import { LogoHustleUpSmall } from "@images/logoHustleUpSmall"
import { LogoHustleUpText } from "@images/logoHustleUpText"
import StripeLogo from "@images/payment/stripe-logo.svg"
import { ArrowRightIcon, Box, ExternalLinkIcon, SquareIcon, X } from "lucide-react"
import { Suspense, useEffect, useEffectEvent, useRef, useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router"

type Steps = "almost-ready" | "migration" | "account-ready"

export function CoachCreationFinalStepPage() {
  const location: { state?: { coachId?: string; stripeReady?: boolean } } = useLocation()
  const coachId: string | undefined = location.state?.coachId

  if (!coachId) {
    return <Navigate to={Path.CoachOnboarding} replace />
  }

  return (
    <Suspense>
      <FinalStepContent coachId={coachId} stripeReady={!!location.state?.stripeReady} />
    </Suspense>
  )
}

function FinalStepContent({ coachId, stripeReady }: { coachId: string; stripeReady: boolean }) {
  const navigate = useNavigate()
  const { setTheme } = useTheme()
  const user = useUser()
  const [step, setStep] = useState<Steps>(
    user.stripeAccountStatus === StripeAccountStatus.UNDEFINED ? "almost-ready" : "account-ready",
  )

  const { data: programsPage } = usePrograms({ programFilter: ProgramFilter.OWNED, size: 100 })
  const ownedPrograms = programsPage.content

  const applyCoachTheme = useEffectEvent(() => {
    setTheme(Theme.CoachLight)
  })
  useEffect(() => {
    applyCoachTheme()
  }, [])

  const handleSkipStripe = () => {
    setStep("account-ready")
  }

  return (
    <div className="relative min-h-screen overflow-auto bg-surface-background-secondary">
      <Button
        variant="outline"
        size="iconMd"
        onClick={() => void navigate(Path.Coach.Root(coachId))}
        className="absolute top-5 right-5 z-10"
      >
        <X />
      </Button>
      <div className="flex justify-center px-12 py-10">
        <div className="flex gap-2">
          <LogoHustleUpSmall color="currentColor" />
          <LogoHustleUpText color="currentColor" />
        </div>
      </div>
      <div className="flex flex-col items-center px-4 pb-16">
        {step === "almost-ready" && (
          <AlmostReadyStep coachId={coachId} onSkip={handleSkipStripe} onNavigate={navigate} />
        )}
        {step === "account-ready" && (
          <AccountReadyStep
            coachId={coachId}
            onNavigate={navigate}
            stripeReady={stripeReady}
            onMigrate={
              ownedPrograms.length > 0
                ? () => {
                    setStep("migration")
                  }
                : undefined
            }
          />
        )}
        {step === "migration" && (
          <MigrationStep
            coachId={coachId}
            programs={ownedPrograms}
            onDone={() => {
              // void navigate(Path.Coach.Offers(coachId))
            }}
          />
        )}
      </div>
    </div>
  )
}

function AlmostReadyStep({
  coachId,
  onSkip,
  onNavigate,
}: {
  coachId: string
  onSkip: () => void
  onNavigate: ReturnType<typeof useNavigate>
}) {
  const onBoardingStripeModalRef = useRef<OnBoardingStripeModalRef>(null)

  return (
    <div className="w-full max-w-200 rounded-3xl border border-surface-border-gray-default bg-surface-background-default p-12">
      <div className="flex flex-col items-center gap-8 text-center">
        <div className="flex flex-col items-center">
          <img src={RoundsCustomIcon} className="mb-2 size-16" />

          <Text fontStyle="HM" className="font-bold text-textOnSurface-default">
            {word("coach.onboarding.complete.almost_ready.title")}
          </Text>
          <Text fontStyle="BM" className="font-semibold text-textOnSurface-muted">
            {word("coach.onboarding.complete.almost_ready.subtitle")}
          </Text>
        </div>

        {/* Stripe info card */}
        <div className="w-full max-w-130 rounded-2xl border border-surface-border-gray-default bg-surface-background-secondary p-6 text-left">
          <div className="flex flex-col gap-4">
            <img src={StripeLogo} width={60} />
            <div className="flex flex-col gap-2">
              <Text fontStyle="LM" className="font-semibold text-textOnSurface-default uppercase">
                {word("coach.onboarding.complete.almost_ready.card_title")}
              </Text>
              <div className="flex flex-col gap-1">
                <Text fontStyle="LS" className="flex flex-row items-center gap-1">
                  <SquareIcon size={12} className="mb-0.5" />{" "}
                  {word("coach.onboarding.complete.almost_ready.item_email")}
                </Text>
                <Text fontStyle="LS" className="flex flex-row items-center gap-1">
                  <SquareIcon size={12} className="mb-0.5" />{" "}
                  {word("coach.onboarding.complete.almost_ready.item_phone")}
                </Text>
                <Text fontStyle="LS" className="flex flex-row items-center gap-1">
                  <SquareIcon size={12} className="mb-0.5" /> {word("coach.onboarding.complete.almost_ready.item_iban")}
                </Text>
                <Text fontStyle="LS" className="flex flex-row items-center gap-1">
                  <SquareIcon size={12} className="mb-0.5" />{" "}
                  {word("coach.onboarding.complete.almost_ready.item_billing")}
                </Text>
              </div>
            </div>
            <div className="flex items-start rounded-2xl bg-surface-background-primary-information-secondary p-5">
              <Text fontStyle="BM" className="text-textOnSurface-subtle">
                {word("coach.onboarding.complete.almost_ready.card_body")}
              </Text>
            </div>
            <Button
              variant="primary"
              size="lg"
              className="w-fit"
              onClick={() => {
                localStorage.setItem(LocalStorageKeys.coach.onboarding.redirect, coachId)
                onBoardingStripeModalRef.current?.show()
              }}
            >
              {word("coach.onboarding.complete.almost_ready.stripe_cta")}
              <ArrowRightIcon />
            </Button>
          </div>
        </div>

        <div className="flex w-full flex-row items-center justify-center gap-3">
          <Button variant="outline" size="lg" onClick={() => void onNavigate(Path.Coach.Root(coachId))}>
            {word("coach.onboarding.complete.almost_ready.view_vitrine")}
            <ExternalLinkIcon />
          </Button>
          <Button variant="ghost" size="lg" onClick={onSkip}>
            {word("coach.onboarding.complete.almost_ready.skip")}
          </Button>
        </div>
      </div>
      <OnBoardingStripeModal ref={onBoardingStripeModalRef} />
    </div>
  )
}

function MigrationStep({
  coachId,
  programs,
  onDone,
}: {
  coachId: string
  programs: UserProgramItemEntity[]
  onDone: () => void
}) {
  const paidPrograms = programs.filter((p) => p.price !== null)
  const freePrograms = programs.filter((p) => p.price === null)
  const onlyPaid = freePrograms.length === 0

  const [selectedFreeIds, setSelectedFreeIds] = useState<string[]>([])
  const attachPrograms = useAttachProgramsToCoach()

  const toggleFree = (id: string) => {
    setSelectedFreeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleSubmit = async () => {
    await attachPrograms.mutateAsync({ coachId, programIds: selectedFreeIds })
    onDone()
  }

  return (
    <div className="w-full max-w-250 rounded-3xl border border-surface-border-gray-default bg-surface-background-default p-12">
      {/* Header */}
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <Box className="size-18 text-interactive-background-primary-faded" />
        <div className="flex flex-col">
          <Text fontStyle="HM" className="font-bold text-textOnSurface-default">
            {onlyPaid
              ? word("coach.onboarding.complete.migration.title_paid_only")
              : word("coach.onboarding.complete.migration.title")}
          </Text>
          <Text fontStyle="BM" className="font-semibold text-textOnSurface-subtle">
            {word("coach.onboarding.complete.migration.subtitle")}
          </Text>
        </div>
      </div>

      {/* Panels */}
      <div
        className={cn(
          "grid gap-6",
          paidPrograms.length > 0 && freePrograms.length > 0 ? "grid-cols-2" : "mx-auto w-full max-w-200 grid-cols-1",
        )}
      >
        {/* Paid programs */}
        {paidPrograms.length > 0 && (
          <div className="flex flex-col gap-4 rounded-2xl bg-surface-background-secondary p-6">
            <div className="flex items-start rounded-2xl bg-surface-background-primary-information-secondary p-5">
              <div className="flex flex-col gap-1">
                <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
                  {word("coach.onboarding.complete.migration.paid_panel_title")}
                </Text>
                <Text fontStyle="BM" className="text-textOnSurface-subtle">
                  {word("coach.onboarding.complete.migration.paid_panel_body")}
                </Text>
              </div>
            </div>
            <div className="flex flex-col gap-2 rounded-2xl border border-surface-border-gray-default bg-surface-background-secondary p-4">
              {paidPrograms.map((p) => (
                <Text key={p.id} fontStyle="BM" className="text-textOnSurface-default">
                  {p.name}
                </Text>
              ))}
            </div>
          </div>
        )}

        {/* Free programs */}
        {freePrograms.length > 0 && (
          <div className="flex flex-col gap-4 rounded-2xl bg-surface-background-secondary p-6">
            <div className="flex items-start rounded-2xl bg-surface-background-primary-information-secondary p-5">
              <div className="flex flex-col gap-1">
                <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
                  {word("coach.onboarding.complete.migration.free_panel_title")}
                </Text>
                <Text fontStyle="BM" className="text-textOnSurface-subtle">
                  {word("coach.onboarding.complete.migration.free_panel_body")}
                </Text>
              </div>
            </div>
            <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
              {word("coach.onboarding.complete.migration.free_select_label")}
            </Text>
            <div className="flex flex-col gap-2">
              {freePrograms.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={selectedFreeIds.includes(p.id)}
                    onCheckedChange={() => {
                      toggleFree(p.id)
                    }}
                    className="size-4"
                  />
                  <Text fontStyle="TS" className="font-semibold text-textOnSurface-subtle">
                    {p.name}
                  </Text>
                </label>
              ))}
            </div>
            <Button
              variant="primary"
              size="lg"
              className="w-fit"
              onClick={() => void handleSubmit()}
              disabled={attachPrograms.isPending}
            >
              {word("global.next")}
              <ArrowRightIcon />
            </Button>
          </div>
        )}
      </div>

      {/* CTA when only paid programs */}
      {onlyPaid && (
        <div className="mt-6">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => void handleSubmit()}
            disabled={attachPrograms.isPending}
          >
            {word("global.next")}
            <ArrowRightIcon />
          </Button>
        </div>
      )}
    </div>
  )
}

function AccountReadyStep({
  coachId,
  onNavigate,
  onMigrate,
  stripeReady,
}: {
  coachId: string
  onNavigate: ReturnType<typeof useNavigate>
  onMigrate?: () => void
  stripeReady: boolean
}) {
  const strings = {
    title: stripeReady
      ? word("coach.onboarding.complete.account_ready.title_stripe_active")
      : word("coach.onboarding.complete.account_ready.title"),
    subtitle: stripeReady
      ? word("coach.onboarding.complete.account_ready.subtitle_stripe_active")
      : word("coach.onboarding.complete.account_ready.subtitle"),
  }

  return (
    <div className="w-full max-w-200 rounded-3xl border border-surface-border-gray-default bg-surface-background-default p-12">
      <div className="flex flex-col items-center gap-8 text-center">
        <div className="flex flex-col items-center">
          <img src={CongratulationsIcon} className="size-36" />
          <Text fontStyle="HM" className="font-bold text-textOnSurface-default">
            {strings.title}
          </Text>
          <Text fontStyle="BM" className="font-semibold text-textOnSurface-muted">
            {strings.subtitle}
          </Text>
        </div>

        <div className="flex w-full items-center justify-center gap-3">
          <Button variant="outline" size="lg" onClick={() => void onNavigate(Path.Coach.Root(coachId))}>
            {word("coach.onboarding.complete.account_ready.view_vitrine")}
            <ExternalLinkIcon />
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              if (onMigrate) {
                onMigrate()
              } else {
                // void onNavigate(Path.Coach.Offers(coachId))
              }
            }}
          >
            {word("coach.onboarding.complete.account_ready.configure_offer")}
          </Button>
        </div>
      </div>
    </div>
  )
}
